import { useState, useEffect, useRef, useCallback } from "react";
import { Shield, Settings2, X } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { getStoredConsent, saveConsent, applyStoredConsent } from "@/lib/analytics";

/**
 * KLYROO Consent Banner — the real mechanism for a visitor's Consent Mode v2 choice.
 * Consent Mode defaults remain DENIED (set in analytics.js); this banner only ever
 * moves states via saveConsent()/window.klyrooConsent. No dark patterns.
 *
 * Reopen anytime via the footer "Privacy Settings" link (dispatches "klyroo:open-privacy").
 */
export default function ConsentBanner() {
  const [open, setOpen] = useState(false);
  const [manage, setManage] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [advertising, setAdvertising] = useState(false);
  const dialogRef = useRef(null);

  const openPrefs = useCallback(() => {
    const c = getStoredConsent();
    setAnalytics(c ? !!c.analytics : false);
    setAdvertising(c ? !!c.advertising : false);
    setManage(true);
    setOpen(true);
  }, []);

  useEffect(() => {
    const stored = getStoredConsent();
    if (stored) { applyStoredConsent(); setOpen(false); }
    else { setOpen(true); }
    window.addEventListener("klyroo:open-privacy", openPrefs);
    return () => window.removeEventListener("klyroo:open-privacy", openPrefs);
  }, [openPrefs]);

  // Focus management: focus first control on open, trap Tab inside the dialog, Escape closes.
  useEffect(() => {
    if (!open) return;
    const node = dialogRef.current;
    if (!node) return;
    const SEL = 'button, [role="switch"], a[href], input, [tabindex]:not([tabindex="-1"])';
    const focusables = () => Array.from(node.querySelectorAll(SEL)).filter((el) => !el.disabled && el.offsetParent !== null);
    const list = focusables();
    (list[0] || node).focus();
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); setOpen(false); setManage(false); return; }
      if (e.key !== "Tab") return;
      const items = focusables();
      if (!items.length) return;
      const first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    node.addEventListener("keydown", onKey);
    return () => node.removeEventListener("keydown", onKey);
  }, [open, manage]);

  if (!open) return null;

  const acceptAll = () => { saveConsent(true, true); setOpen(false); setManage(false); };
  const rejectAll = () => { saveConsent(false, false); setOpen(false); setManage(false); };
  const savePrefs = () => { saveConsent(analytics, advertising); setOpen(false); setManage(false); };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="consent-title"
      data-testid="consent-banner"
      ref={dialogRef}
      tabIndex={-1}
      className="fixed inset-x-0 bottom-0 z-[120] p-3 sm:p-5 outline-none"
    >
      <div className="relative mx-auto max-w-4xl max-h-[85vh] overflow-y-auto rounded-2xl border border-white/10 bg-klyroo-surface/95 backdrop-blur-xl shadow-2xl">
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-klyroo-cyan/5 to-klyroo-violet/10" />
        <div className="relative p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-klyroo-cyan/10 flex items-center justify-center text-klyroo-cyan shrink-0">
              <Shield size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <h2 id="consent-title" className="font-display text-lg font-bold text-white">Your Privacy Matters</h2>
              <p className="text-slate-300 text-sm mt-1.5 leading-relaxed">
                KLYROO uses cookies and similar technologies to understand how visitors use the site,
                measure performance, and improve our services. You can choose which categories you allow.
              </p>
            </div>
            {manage && (
              <button aria-label="Close preferences" data-testid="consent-close"
                onClick={() => (getStoredConsent() ? setOpen(false) : setManage(false))}
                className="text-slate-400 hover:text-white shrink-0">
                <X size={18} />
              </button>
            )}
          </div>

          {manage && (
            <div className="mt-5 space-y-3" data-testid="consent-preferences">
              <Category
                title="Strictly Necessary" locked checked
                desc="Required for the site to function (security, core navigation). Always on."
                testid="consent-necessary" />
              <Category
                title="Analytics" checked={analytics} onChange={setAnalytics}
                desc="Helps us measure page views and engagement (Google Analytics 4). Controls analytics_storage."
                testid="consent-analytics-toggle" />
              <Category
                title="Advertising" checked={advertising} onChange={setAdvertising}
                desc="Enables advertising measurement and personalization. Controls ad_storage, ad_user_data and ad_personalization."
                testid="consent-advertising-toggle" />
            </div>
          )}

          <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
            {!manage ? (
              <>
                <button onClick={acceptAll} data-testid="consent-accept-all"
                  className="order-1 flex-1 sm:flex-none px-6 py-2.5 rounded-xl bg-klyroo-cyan text-black font-semibold hover:bg-white transition-colors">
                  Accept All
                </button>
                <button onClick={rejectAll} data-testid="consent-reject-all"
                  className="order-2 flex-1 sm:flex-none px-6 py-2.5 rounded-xl bg-slate-200 text-black font-semibold hover:bg-white transition-colors">
                  Reject All
                </button>
                <button onClick={() => setManage(true)} data-testid="consent-manage"
                  className="order-3 sm:ml-auto px-4 py-2.5 rounded-xl border border-white/15 text-slate-200 hover:bg-white/10 font-medium inline-flex items-center justify-center gap-1.5 transition-colors">
                  <Settings2 size={15} /> Manage Preferences
                </button>
              </>
            ) : (
              <>
                <button onClick={savePrefs} data-testid="consent-save"
                  className="order-1 px-5 py-2.5 rounded-xl bg-gradient-to-r from-klyroo-cyan to-klyroo-violet text-black font-semibold hover:opacity-90 transition-opacity">
                  Save Preferences
                </button>
                <button onClick={acceptAll} data-testid="consent-accept-all-2"
                  className="order-2 px-5 py-2.5 rounded-xl bg-white/5 border border-white/15 text-white font-medium hover:bg-white/10 transition-colors">
                  Accept All
                </button>
                <button onClick={rejectAll} data-testid="consent-reject-all-2"
                  className="order-3 px-5 py-2.5 rounded-xl bg-white/5 border border-white/15 text-white font-medium hover:bg-white/10 transition-colors">
                  Reject All
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Category({ title, desc, checked, onChange, locked, testid }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-xl bg-klyroo-bg/60 border border-white/5 p-4">
      <div className="min-w-0">
        <div className="text-white text-sm font-semibold">{title}{locked && <span className="ml-2 text-[10px] font-mono uppercase tracking-widest text-klyroo-cyan">Always on</span>}</div>
        <p className="text-slate-400 text-xs mt-1 leading-relaxed">{desc}</p>
      </div>
      <Switch
        checked={locked ? true : checked}
        disabled={locked}
        onCheckedChange={locked ? undefined : onChange}
        data-testid={testid}
        aria-label={title}
      />
    </div>
  );
}
